import { useMemo } from 'react';

import { ctw } from '@/common';
import { QuestionMarkTooltip } from '../components/QuestionMarkTooltip';
import { ReportWrapper } from '../components/ReportWrapper';

type DigitalFootprintTag = {
  name: string;
  level: 'positive' | 'moderate' | 'negative';
};

type TagsSectionProps = {
  tags?: DigitalFootprintTag[] | null;
};

export const getTagStyles = (level: DigitalFootprintTag['level']) => {
  const normalizedLevel = level.toLowerCase();

  switch (normalizedLevel) {
    case 'positive':
      return 'bg-green-100 text-green-700';
    case 'negative':
      return 'bg-red-100 text-red-700';
    case 'moderate':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export const sortTagsBySeverity = (
  tags: DigitalFootprintTag[] | undefined | null,
): DigitalFootprintTag[] => {
  if (!tags) {
    return [];
  }

  const severityOrder: Record<string, number> = {
    negative: 0,
    critical: 0,
    high: 0,
    warning: 1,
    medium: 1,
    moderate: 1,
    positive: 2,
    low: 2,
  };

  return [...tags].sort((a, b) => {
    const aOrder = severityOrder[a.level.toLowerCase()] ?? 999;
    const bOrder = severityOrder[b.level.toLowerCase()] ?? 999;

    return aOrder - bOrder;
  });
};

export const TagsSection: React.FC<TagsSectionProps> = ({ tags }) => {
  const sortedTags = sortTagsBySeverity(tags);

  const tagColumns = useMemo(() => {
    if (sortedTags.length === 0) return [];

    const numColumns = 3;
    const tagsPerColumn = Math.ceil(sortedTags.length / numColumns);

    const columns: DigitalFootprintTag[][] = [];

    for (let i = 0; i < numColumns; i++) {
      const startIndex = i * tagsPerColumn;
      const columnTags = sortedTags.slice(startIndex, startIndex + tagsPerColumn);

      if (columnTags.length > 0) {
        columns.push(columnTags);
      }
    }

    return columns;
  }, [sortedTags]);

  if (!tags || sortedTags.length === 0) {
    return null;
  }

  return (
    <ReportWrapper showTooltip={false}>
      <div className="mb-3 flex items-center gap-1 pt-10">
        <h4 className="text-sm font-semibold text-stone-300">TAGS</h4>

        <QuestionMarkTooltip
          text="Tags associated with the digital footprint, indicating various attributes or risk levels."
          className="ml-2"
        />
      </div>
      <div className="flex gap-4">
        {tagColumns.map((column, colIndex) => (
          <div key={colIndex} className="flex flex-1 cursor-default flex-col gap-2">
            {column.map((tag, tagIndex) => {
              const styles = getTagStyles(tag.level);

              return (
                <div key={tagIndex} className="flex">
                  <span
                    className={ctw(
                      'inline-block max-w-60 truncate rounded-sm border-0 px-1.5 py-0.5 text-xs font-normal 2xl:max-w-80',
                      styles,
                    )}
                    title={tag.name}
                  >
                    {tag.name}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </ReportWrapper>
  );
};
